import type { SelectReservation } from '@workspace/db/extra'
import { useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  type BatchResult,
  editedReservationInput,
  isReservationConflict,
  type ReservationInput,
  useAddReservation,
  useAddReservationsBatch,
  useDeleteReservation,
  useEditReservationsBatch,
  useRefreshReservations,
  useReservations,
} from '../../../features/locations/use-reservations'
import { useIdOrCode } from '../../use-id-or-code'
import { Btn, Chip, ConfirmDialog, Dialog, Empty, Field, Inline, inputCls, SubCrumbs, td, th } from './controls'
import { Bar, mono, Note, Panel, SkelPanels } from './ui'

const TYPES = ['workstation', 'printer', 'other']
const MAC_RE = /^([0-9a-f]{2}[:-]){5}[0-9a-f]{2}$/i
const statusTone = (s: string | null) => (s === 'ACTIVE' ? 'ok' : s === 'CREATING' ? 'warn' : 'off')
const FORCE_BODY =
  'Another reservation already uses this MAC or IP address. Forcing it overwrites the existing reservation.'

function TypeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <>
      <input className={inputCls} list="v2-res-types" value={value} onChange={(e) => onChange(e.target.value)} />
      <datalist id="v2-res-types">
        {TYPES.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </>
  )
}

function AddDialog({ idOrCode, code, onClose }: { idOrCode: string; code: string; onClose: () => void }) {
  const add = useAddReservation()
  const [mac, setMac] = useState('')
  const [type, setType] = useState('workstation')
  const [vlan, setVlan] = useState('100')
  const [conflict, setConflict] = useState(false)
  const valid = MAC_RE.test(mac.trim()) && type.trim() !== '' && vlan.trim() !== ''

  const submit = (force: boolean) =>
    add.mutate(
      { idOrCode, reservation: { code, mac: mac.trim().toLowerCase(), type: type.trim(), vlan, force } },
      { onSuccess: onClose, onError: (e) => setConflict(isReservationConflict(e)) },
    )

  return (
    <>
      <Dialog
        open={!conflict}
        title="Add reservation"
        onClose={onClose}
        onSubmit={() => valid && !add.isPending && submit(false)}
        footer={
          <>
            <Btn onClick={onClose} disabled={add.isPending}>
              Cancel
            </Btn>
            <Btn variant="primary" onClick={() => submit(false)} disabled={!valid || add.isPending}>
              {add.isPending ? 'Adding...' : 'Add'}
            </Btn>
          </>
        }
      >
        <Field label="MAC" hint="aa:bb:cc:dd:ee:ff">
          <input className={inputCls} value={mac} onChange={(e) => setMac(e.target.value)} style={mono} />
        </Field>
        <Field label="Type">
          <TypeInput value={type} onChange={setType} />
        </Field>
        <Field label="VLAN">
          <input
            className={inputCls}
            style={mono}
            inputMode="numeric"
            value={vlan}
            onChange={(e) => setVlan(e.target.value)}
          />
        </Field>
        {add.error && !conflict ? <Inline tone="bad">{apiErrorMessage(add.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={conflict}
        title="Reservation conflicts"
        body={FORCE_BODY}
        confirmLabel="Force add"
        danger
        pending={add.isPending}
        error={add.error && !isReservationConflict(add.error) ? apiErrorMessage(add.error) : null}
        onConfirm={() => submit(true)}
        onClose={() => setConflict(false)}
      />
    </>
  )
}

/** Runs a batch from index `from`; on a 409 stops and offers to force that item and continue. */
function useBatchRunner(run: (from: number, force: boolean) => Promise<BatchResult>, onDone: () => void) {
  const [conflictAt, setConflictAt] = useState<number | null>(null)
  const [done, setDone] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const go = async (from: number, force: boolean) => {
    setPending(true)
    setError(null)
    try {
      const res = await run(from, force)
      setDone(from + res.processedCount)
      if (res.conflict) setConflictAt(from + res.conflict.index)
      else {
        setConflictAt(null)
        onDone()
      }
    } catch (e) {
      setError(apiErrorMessage(e))
    } finally {
      setPending(false)
    }
  }
  return { conflictAt, done, error, pending, go, clear: () => setConflictAt(null) }
}

function BatchAddDialog({ idOrCode, code, onClose }: { idOrCode: string; code: string; onClose: () => void }) {
  const batch = useAddReservationsBatch()
  const [text, setText] = useState('')
  const parsed: ReservationInput[] = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [mac = '', type = 'workstation', vlan = '100'] = l.split(/[,\s]+/)
      return { code, mac: mac.toLowerCase(), type, vlan }
    })
  const invalid = parsed.findIndex((p) => !MAC_RE.test(p.mac))
  const runner = useBatchRunner(
    (from, force) =>
      batch.mutateAsync({
        idOrCode,
        reservations: parsed.slice(from).map((r, n) => (n === 0 && force ? { ...r, force: true } : r)),
      }),
    onClose,
  )
  const conflicted = runner.conflictAt !== null
  const canRun = parsed.length > 0 && invalid < 0 && !runner.pending

  return (
    <>
      <Dialog
        open={!conflicted}
        title="Add reservations in bulk"
        onClose={onClose}
        onSubmit={() => canRun && runner.go(0, false)}
        footer={
          <>
            <Btn onClick={onClose} disabled={runner.pending}>
              Cancel
            </Btn>
            <Btn variant="primary" onClick={() => runner.go(0, false)} disabled={!canRun}>
              {runner.pending ? 'Adding...' : `Add ${parsed.length || ''}`.trim()}
            </Btn>
          </>
        }
      >
        <Field label="MAC, type, VLAN per line" hint="type + vlan optional (workstation, 100)">
          <textarea
            className={`${inputCls} h-40 py-1.5`}
            style={mono}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="aa:bb:cc:dd:ee:01, workstation, 100"
          />
        </Field>
        {invalid >= 0 && <Inline tone="bad">line {invalid + 1}: invalid MAC</Inline>}
        {runner.error && <Inline tone="bad">{runner.error}</Inline>}
        {runner.done > 0 && !runner.error && <Inline tone="muted">{runner.done} added so far</Inline>}
      </Dialog>
      <ConfirmDialog
        open={conflicted}
        title={`Line ${(runner.conflictAt ?? 0) + 1} conflicts`}
        body={`${FORCE_BODY} ${runner.done} added before the conflict.`}
        confirmLabel="Force and continue"
        danger
        pending={runner.pending}
        error={runner.error}
        onConfirm={() => runner.go(runner.conflictAt ?? 0, true)}
        onClose={runner.clear}
      />
    </>
  )
}

function EditDialog({
  idOrCode,
  code,
  res,
  onClose,
}: {
  idOrCode: string
  code: string
  res: SelectReservation
  onClose: () => void
}) {
  const edit = useEditReservationsBatch()
  const [mac, setMac] = useState(res.mac ?? '')
  const [type, setType] = useState(res.type ?? 'workstation')
  const [conflict, setConflict] = useState(false)
  const valid = MAC_RE.test(mac.trim()) && type.trim() !== ''

  const submit = (force: boolean) =>
    edit.mutate(
      {
        idOrCode,
        changes: [
          { oldMac: res.mac ?? '', next: editedReservationInput(code, mac.trim().toLowerCase(), type.trim(), force) },
        ],
      },
      { onSuccess: (r) => (r.conflict ? setConflict(true) : onClose()) },
    )

  return (
    <>
      <Dialog
        open={!conflict}
        title="Edit reservation"
        onClose={onClose}
        onSubmit={() => valid && !edit.isPending && submit(false)}
        footer={
          <>
            <Btn onClick={onClose} disabled={edit.isPending}>
              Cancel
            </Btn>
            <Btn variant="primary" onClick={() => submit(false)} disabled={!valid || edit.isPending}>
              {edit.isPending ? 'Saving...' : 'Save'}
            </Btn>
          </>
        }
      >
        <p className="text-[12px] text-[var(--color-muted)]">
          Edit replaces the entry: old one removed, new one created on VLAN 100.
        </p>
        <Field label="MAC">
          <input className={inputCls} value={mac} onChange={(e) => setMac(e.target.value)} style={mono} />
        </Field>
        <Field label="Type">
          <TypeInput value={type} onChange={setType} />
        </Field>
        {edit.error ? <Inline tone="bad">{apiErrorMessage(edit.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={conflict}
        title="Reservation conflicts"
        body={FORCE_BODY}
        confirmLabel="Force save"
        danger
        pending={edit.isPending}
        onConfirm={() => {
          setConflict(false)
          submit(true)
        }}
        onClose={() => setConflict(false)}
      />
    </>
  )
}

export function SignalReservations() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const reservations = useReservations(idOrCode)
  const del = useDeleteReservation()
  const refresh = useRefreshReservations()

  const [addOpen, setAddOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [editRes, setEditRes] = useState<SelectReservation | null>(null)
  const [deleteRes, setDeleteRes] = useState<SelectReservation | null>(null)
  const [filter, setFilter] = useState('')

  const crumbs = <SubCrumbs idOrCode={idOrCode} section="network" page="reservations" />

  if (location.isLoading || reservations.isLoading)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Reservations" />
        <div className="overflow-auto">
          <SkelPanels n={1} />
        </div>
      </div>
    )
  const loc = location.data
  if (location.error || reservations.error || !loc)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Reservations" />
        <Note error>
          Failed to load reservations: {(location.error ?? reservations.error)?.message ?? 'Not found.'}
        </Note>
      </div>
    )

  const code = loc.aptosStoreCode ?? loc.code ?? idOrCode
  const all = reservations.data ?? []
  const f = filter.trim().toLowerCase()
  const list = f ? all.filter((r) => [r.name, r.mac, r.ip, r.type].some((v) => v?.toLowerCase().includes(f))) : all

  return (
    <div className="flex h-full flex-col">
      <Bar
        crumbs={crumbs}
        title="Reservations"
        meta={
          <span className="text-[12px] text-[var(--color-muted)]" style={mono}>
            {all.length} total
          </span>
        }
        right={
          <>
            <input
              className={`${inputCls} w-44`}
              placeholder="Filter mac / ip / name"
              aria-label="Filter reservations"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={mono}
            />
            <Btn disabled={refresh.isPending} onClick={() => refresh.mutate({ idOrCode, aptosCode: code })}>
              {refresh.isPending ? 'Refreshing...' : 'Refresh'}
            </Btn>
            <Btn onClick={() => setBulkOpen(true)}>Add in bulk</Btn>
            <Btn variant="primary" onClick={() => setAddOpen(true)}>
              Add
            </Btn>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <Panel title="DHCP reservations">
          {refresh.error && (
            <div className="border-b border-[var(--color-border)] px-3 py-1.5">
              <Inline tone="bad">{apiErrorMessage(refresh.error)}</Inline>
            </div>
          )}
          {list.length === 0 ? (
            <Empty>{all.length === 0 ? 'No reservations yet.' : 'No reservations match the filter.'}</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse" aria-label="Reservations">
                <thead>
                  <tr>
                    {['Name', 'MAC', 'IP', 'Type', 'Status', ''].map((h) => (
                      <th key={h || 'actions'} scope="col" className={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.id} className="hover:bg-[var(--color-surface-2)]">
                      <td className={td}>{r.name ?? '-'}</td>
                      <td className={td} style={mono}>
                        {r.mac}
                      </td>
                      <td className={td} style={mono}>
                        {r.ip ?? '-'}
                      </td>
                      <td className={`${td} text-[var(--color-muted)]`}>{r.type ?? '-'}</td>
                      <td className={td}>
                        <Chip tone={statusTone(r.status)}>{r.status ?? 'unknown'}</Chip>
                      </td>
                      <td className={`${td} text-right`}>
                        <span className="inline-flex gap-1">
                          <Btn onClick={() => setEditRes(r)}>Edit</Btn>
                          <Btn variant="danger" onClick={() => setDeleteRes(r)}>
                            Delete
                          </Btn>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {addOpen && <AddDialog idOrCode={idOrCode} code={code} onClose={() => setAddOpen(false)} />}
      {bulkOpen && <BatchAddDialog idOrCode={idOrCode} code={code} onClose={() => setBulkOpen(false)} />}
      {editRes && <EditDialog idOrCode={idOrCode} code={code} res={editRes} onClose={() => setEditRes(null)} />}
      <ConfirmDialog
        open={deleteRes !== null}
        title="Delete reservation?"
        body={
          <>
            <span style={mono}>{deleteRes?.mac}</span> loses its fixed address and may receive a different one.
          </>
        }
        confirmLabel="Delete"
        danger
        pending={del.isPending}
        error={del.error ? apiErrorMessage(del.error) : null}
        onConfirm={() =>
          deleteRes?.mac && del.mutate({ idOrCode, mac: deleteRes.mac }, { onSuccess: () => setDeleteRes(null) })
        }
        onClose={() => {
          del.reset()
          setDeleteRes(null)
        }}
      />
    </div>
  )
}
